import { hasSupabaseConfig, supabase } from '../../../shared/api/supabaseClient.js';

const BRIDGE_ERROR_MESSAGES = {
  authentication_required: '로그인 세션을 확인할 수 없습니다. 다시 로그인해주세요.',
  active_union_membership_required: '활성 유니온 가입 정보가 필요합니다.',
  union_manager_required: '대표 또는 부대표만 수집기 토큰을 관리할 수 있습니다.',
  bridge_label_too_long: '수집기 이름은 80자 이하로 입력해주세요.',
};

export const RAID_BRIDGE_ENDPOINT = `${String(import.meta.env.VITE_SUPABASE_URL || '').replace(/\/$/, '')}/functions/v1/raid-bridge-sync`;

function requireClient() {
  if (!hasSupabaseConfig || !supabase) {
    throw new Error('서비스 연결 설정을 확인할 수 없습니다.');
  }
  return supabase;
}

function createBridgeError(error, fallback) {
  const raw = String(error?.message || '');
  const code = Object.keys(BRIDGE_ERROR_MESSAGES).find((item) => raw.includes(item));
  const nextError = new Error(BRIDGE_ERROR_MESSAGES[code] || fallback || '레이드 수집기 정보를 처리하지 못했습니다.');
  nextError.code = code || error?.code || 'unknown_error';
  return nextError;
}

async function requireAuthenticatedClient() {
  const client = requireClient();
  const { data, error } = await client.auth.getSession();
  if (error || !data?.session?.access_token) {
    throw createBridgeError({ message: 'authentication_required' });
  }
  return client;
}

function normalizeBatch(batch) {
  return {
    id: String(batch?.id || ''),
    seasonNo: Number(batch?.season_no || 0),
    mode: String(batch?.sync_mode || ''),
    snapshotComplete: Boolean(batch?.snapshot_complete),
    recordCount: Number(batch?.record_count || 0),
    upsertedCount: Number(batch?.upserted_count || 0),
    removedCount: Number(batch?.removed_count || 0),
    sourceCursor: String(batch?.source_cursor || ''),
    createdAt: batch?.created_at || null,
  };
}

function normalizeSeasons(rawSetup, activeSeasonNo) {
  const rows = Array.isArray(rawSetup?.seasons) ? rawSetup.seasons : [];
  const seasons = rows
    .map((season) => ({
      seasonNo: Number(season?.season_no || 0),
      status: String(season?.status || 'draft'),
      autoCreate: false,
    }))
    .filter((season) => Number.isInteger(season.seasonNo) && season.seasonNo > 0);

  if (seasons.length === 0 && activeSeasonNo) {
    seasons.push({ seasonNo: activeSeasonNo, status: 'active', autoCreate: false });
  }

  return seasons.filter((season) => season.status === 'active').sort((a, b) => b.seasonNo - a.seasonNo);
}

function normalizeStatus(raw, rawSetup) {
  const value = raw && typeof raw === 'object' ? raw : {};
  const token = value.active_token && typeof value.active_token === 'object' ? value.active_token : null;
  const activeSeasonNo = value.active_season_no ? Number(value.active_season_no) : null;
  const seasons = normalizeSeasons(rawSetup, activeSeasonNo);
  return {
    unionId: String(value.union_id || ''),
    unionName: String(value.union_name || ''),
    viewerRole: String(value.viewer_role || ''),
    canManage: Boolean(value.can_manage),
    activeSeasonNo,
    seasons,
    suggestedSeasonNo: seasons.find((season) => season.autoCreate)?.seasonNo || null,
    currentRecordCount: Number(value.current_record_count || 0),
    activeToken: token ? {
      id: String(token.id || ''),
      revision: String(token.revision || token.id || ''),
      token: String(token.token || ''),
      shareReady: Boolean(token.share_ready && token.token),
      label: String(token.label || ''),
      createdAt: token.created_at || null,
      lastUsedAt: token.last_used_at || null,
    } : null,
    recentBatches: (Array.isArray(value.recent_batches) ? value.recent_batches : []).map(normalizeBatch),
  };
}

export async function loadRaidBridgeStatus() {
  const client = await requireAuthenticatedClient();
  const statusResult = await client.rpc('get_union_bridge_status');
  if (statusResult.error) {
    throw createBridgeError(statusResult.error, '레이드 수집기 상태를 불러오지 못했습니다.');
  }

  if (!statusResult.data?.can_manage) {
    return normalizeStatus(statusResult.data, null);
  }

  const seasonResult = await client.rpc('get_union_raid_setup', { requested_season_no: null });
  if (seasonResult.error) {
    throw createBridgeError(seasonResult.error, '등록된 레이드 차수 목록을 불러오지 못했습니다.');
  }
  return normalizeStatus(statusResult.data, seasonResult.data);
}

export async function rotateRaidBridgeToken(label = '') {
  const client = await requireAuthenticatedClient();
  const { data, error } = await client.rpc('rotate_union_bridge_token', {
    target_label: String(label || '').trim() || null,
  });
  if (error) throw createBridgeError(error, '공용 수집 토큰을 발급하지 못했습니다.');
  if (!data?.token) throw createBridgeError(null, '발급된 공용 수집 토큰을 확인하지 못했습니다.');
  return {
    id: String(data.id || ''),
    revision: String(data.revision || data.id || ''),
    token: String(data.token),
    shareReady: Boolean(data.share_ready ?? true),
    label: String(data.label || ''),
    createdAt: data.created_at || null,
  };
}

export async function revokeRaidBridgeToken() {
  const client = await requireAuthenticatedClient();
  const { data, error } = await client.rpc('revoke_union_bridge_token');
  if (error) throw createBridgeError(error, '공용 수집 토큰을 해제하지 못했습니다.');
  return Boolean(data);
}
